import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Form, Button, Row, Col } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import Select from 'react-select';

const EditPatentForm = () => {
  const { id } = useParams(); // Get the patent ID from the URL
  const [formData, setFormData] = useState({
    departmentId: "",
    title: "",
    applicationNumber: "",
    year: "",
    filingDate: "",
    status: "",
    publishedDate: "",
    ferSubmittedDate: "",
    hearingDate: "",
    grantedDate: "",
    patentNumber: "",
    renewalDate: "",
    technologyReadinessLevel: "",
    inventors: [],
    otherInventors: "",
    otherInventor: "",
    commercialized: "no", // Default value
    commercializationPercentage: "", // Initialize as an empty string
    commercializationDescription: "", // Initialize as an empty string
    fundsGenerated: "", // Initialize as an empty string
    commercializationDate: "", // Initialize as an empty string
    startDate: "", // Initialize as an empty string
    filePath: "", // This will store the file path as a string
    commercializationid: null  // To store the ID of the commercialization entry
  });

  const [departments, setDepartments] = useState([]);
  const [inventorOptions, setInventorOptions] = useState([]);
  const [selectedInventors, setSelectedInventors] = useState([]);
  const [file, setFile] = useState(null); // State for file upload

  // Helper function to format date
  const formatDate = (date) => {
    if (!date) return ''; // Return an empty string if the date is null or undefined
    const parsedDate = new Date(date);
    return parsedDate.toISOString().split('T')[0]; // Convert to YYYY-MM-DD
  };

  // Fetch departments
  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/departments`);
        setDepartments(response.data);
      } catch (error) {
        console.error('Error fetching departments:', error);
      }
    };
    fetchDepartments();
  }, []);

  // Fetch inventors
  useEffect(() => {
    const fetchInventors = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/users`);
        console.log("Inventors Data:", response.data); // Log the inventors data
        setInventorOptions(response.data);
      } catch (error) {
        console.error('Error fetching inventors:', error);
      }
    };
    fetchInventors();
  }, []);

  // Fetch patent details and commercialization data
  useEffect(() => {
    const fetchPatentDetails = async () => {
      if (id) {
        try {
          const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/patents/${id}`);
          const data = response.data;

          // Log the entire response for debugging
          console.log("Patent Data:", data);

          // Split inventors string into an array
          const inventors = data.inventors ? data.inventors.split(', ') : [];
          const options = inventorOptions.map((inventor) => ({
            value: inventor.userId,
            label: `${inventor.username} (${inventor.userId})`,
          }));

          // Map selected inventors based on the fetched data
          const selectedInventors = inventors.map((inventorId) => {
            const matchingInventor = options.find((inv) => inv.value === inventorId.trim());
            return matchingInventor ? matchingInventor : null;
          }).filter(Boolean); // Remove any null values

          setFormData({
            departmentId: data.department || "",
            title: data.title || "",
            applicationNumber: data.applicationNumber || "",
            year: data.year || "",
            filingDate: formatDate(data.filingDate),
            status: data.status || "",
            publishedDate: formatDate(data.publishedDate),
            ferSubmittedDate: formatDate(data.ferSubmittedDate),
            hearingDate: formatDate(data.hearingDate),
            grantedDate: formatDate(data.grantedDate),
            patentNumber: data.patentNumber || "",
            renewalDate: formatDate(data.renewalDate),
            technologyReadinessLevel: data.technologyReadinessLevel || "",
            otherInventors: data.otherInventors || "",
            commercialized: data.commercialized === "yes" ? "yes" : "no", // Adjusted to handle string values
            filePath: data.filePath || "", // Initialize filePath from existing data
            commercializationid: data.commercializationid || null // Initialize commercialization ID
          });

          setSelectedInventors(selectedInventors); // Update selected inventors for react-select

          // Fetch commercialization data if commercialized is "yes"
          if (data.commercialized === "yes") {
            const commercializationResponse = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/commercializations/fetch/${id}`);
            const commercializationData = commercializationResponse.data;

            // Log the commercialization data for debugging
            console.log("Commercialization Data:", commercializationData);

            // Check if commercializationData is not null and has the expected structure
            if (commercializationData) {
              setFormData((prevData) => ({
                ...prevData,
                commercializationid: commercializationData.commercializationid || null,
                commercializationDescription: commercializationData.commercializationDescription || "",
                commercializationPercentage: commercializationData.commercializationPercentage || "",
                fundsGenerated: commercializationData.fundsGenerated || "",
                commercializationDate: commercializationData.commercializationDate ? formatDate(commercializationData.commercializationDate) : "",
                startDate: commercializationData.startDate ? formatDate(commercializationData.startDate) : "",
                filePath: commercializationData.filePath || "", // Set the file path from commercialization data
              }));
            } else {
              console.error("No commercialization data found for this patent.");
            }
          }
        } catch (error) {
          console.error("Error fetching patent details:", error);
        }
      }
    };
    fetchPatentDetails();
  }, [id, inventorOptions]); // Ensure this runs after inventorOptions are set

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleInventorChange = (option) => {
    setSelectedInventors(option);
    const inventorsList = option.map((inventor) => inventor.value);
    setFormData((prev) => ({
      ...prev,
      inventors: inventorsList,
    }));
  };

  const handleFileUpload = (event) => {
    setFile(event.target.files[0]); // Store the selected file
    setFormData((prev) => ({
      ...prev,
      filePath: event.target.files[0] ? event.target.files[0].name : "", // Update filePath with the file name
    }));
  };

  const handlePatentSubmit = async (event) => {
    event.preventDefault();

    try {
      let filePath = "";
      if (file) {
        // Create a FormData object to hold the file
        const formData = new FormData();
        formData.append('file', file);

        // Upload the file to your storage and get the file path
        const uploadResponse = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/upload`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        filePath = uploadResponse.data; // Assuming the response contains the file path
      }

      // Ensure inventors is always an array
      const inventorsList = Array.isArray(formData.inventors) ? formData.inventors : [];

      const submitData = {
        ...formData,
        department: formData.departmentId, // Store departmentId in department (as VARCHAR)
        patentNumber: formData.patentNumber, // Include the patent number in the request body
        renewalDate: formData.renewalDate, // Include the renewal date in the request body
        commercialized: formData.commercialized === 'yes' ? "yes" : "no", // Convert to string
        inventors: inventorsList.join(', '), // Convert array to string
        filePath: filePath || formData.filePath, // Include the file path in the submission
      };

      if (id) {
        // Update Patent Data
        await axios.put(`${process.env.REACT_APP_API_BASE_URL}/api/patents/${id}`, submitData);
        alert('Patent updated successfully!');

        // Check if commercialization data should be updated or created
        if (formData.commercialized === 'yes') {
          
          if (formData.commercializationid) {
            // Update existing commercialization
            const commercializationData = {
              commercializationDescription: formData.commercializationDescription,
              commercializationPercentage: formData.commercializationPercentage,
              fundsGenerated: formData.fundsGenerated,
              commercializationDate: formData.commercializationDate,
              startDate: formData.startDate,
              filePath: filePath || formData.filePath, // Use the new file path if uploaded
            };

            await axios.put(`${process.env.REACT_APP_API_BASE_URL}/api/commercializations/${formData.commercializationid}`, commercializationData);
            alert('Commercialization data updated successfully!');
          } else {
            // Create a new commercialization entry
            const newCommercializationData = new FormData();
            newCommercializationData.append("commercializationDescription", formData.commercializationDescription);
        newCommercializationData.append("commercializationPercentage", formData.commercializationPercentage);
        newCommercializationData.append("fundsGenerated", formData.fundsGenerated);
        newCommercializationData.append("commercializationDate", formData.commercializationDate);
        newCommercializationData.append("startDate", formData.startDate);
        
        // If a new file is uploaded, use it; otherwise, keep the existing filePath
        if (file) {
            newCommercializationData.append("file", file);
        }

        await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/commercializations/${id}`, newCommercializationData, {
            headers: { "Content-Type": "multipart/form-data" }
        });

        alert('Commercialization data created successfully!');
    }
    } else {
        // If commercialized is set to 'no', you might want to delete the commercialization entry
        if (formData.commercializationid) {
            await axios.delete(`${process.env.REACT_APP_API_BASE_URL}/api/commercializations/${formData.commercializationid}`);
            alert('Commercialization data deleted successfully!');
        }
    }
      } else {
        await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/patents`, submitData);
        alert('Patent created successfully!');
      }
    } catch (error) {
      if (error.response) {
        console.error('Server responded with:', error.response.data);
        alert(`Failed to submit form: ${error.response.data.message || error.response.data}`);
      } else if (error.request) {
        console.error('No response received', error.request);
        alert('Failed to submit form');
      } else {
        console.error('Error:', error);
      }
    }
  };

  const handleOtherInventorChange = (e) => {
    setFormData({
      ...formData,
      otherInventor: e.target.value,
    });
  };

  const addOtherInventor = () => {
    if (formData.otherInventor && !formData.otherInventors.includes(formData.otherInventor)) {
      setFormData((prev) => ({
        ...prev,
        otherInventors: prev.otherInventors + (prev.otherInventors ? ", " : "") + formData.otherInventor,
        otherInventor: "",
      }));
    }
  };

  const readinessLevels = [
    '1. Basic Principles Observed',
    '2. Technology Concept Formulated',
    '3. Experimental Proof of Concept',
    '4. Technology Validated in Lab',
    '5. Technology Validated in Relevant Environment',
    '6. System Prototype Demonstrated in Relevant Environment',
    '7. System Prototype Demonstrated in Operational Environment',
    '8. System Completed and Qualified',
    '9. Actual System Proven in Operational Environment'
  ];

  return (
    <div className="patent-form-container d-flex justify-content-center align-items-center min-vh-100 bg-light">
      <Form className="patent-form bg-white p-4 rounded shadow" onSubmit={handlePatentSubmit}>
        <h2 className="text-center mb-4">{id ? 'Edit Patent' : 'Create Patent'}</h2>

        <div className="mb-3">
          <label className="form-label">Department:</label>
          <select
            name="departmentId"
            className="form-select"
            value={formData.departmentId}
            onChange={handleChange}
            required
          >
            <option value="">Select Department</option>
            {departments.map((dept) => (
              <option key={dept.deptId} value={dept.deptId}>
                {dept.deptName} ({dept.deptId})
              </option>
            ))}
          </select>
        </div>
        <div className="mb-3">
          <label className="form-label">Patent Title:</label>
          <input
            type="text"
            name="title"
            className="form-control"
            value={formData.title}
            onChange={handleChange}
            required
          />
        </div>

        {/* Application Info */}
        <div className="row mb-3">
          <div className="col">
            <label className="form-label">Application Number:</label>
            <input
              type="text"
              name="applicationNumber"
              className="form-control"
              value={formData.applicationNumber}
              onChange={handleChange}
              required
            />
          </div>
          <div className="col">
            <label className="form-label">Year:</label>
            <input
              type="text"
              name="year"
              className="form-control"
              value={formData.year}
              onChange={handleChange}
              required
            />
          </div>
          <div className="col">
            <label className="form-label">Filing Date:</label>
            <input
              type="date"
              name="filingDate"
              className="form-control"
              value={formData.filingDate}
              onChange={handleChange}
              required
            />
          </div>
        </div>

        {/* Inventors, Status */}
        <Row>
          <Col>
            <label className="form-label">Inventors:</label>
            <Select
              options={inventorOptions.map((inventor) => ({
                value: inventor.userId,
                label: `${inventor.username} (${inventor.userId})`,
              }))}
              onChange={handleInventorChange}
              isMulti
              value={selectedInventors}
            />
            <table>
              <thead>
                <tr>
                  <th>Inventor ID</th>
                  <th>Inventor Name</th>
                </tr>
              </thead>
              <tbody>
                {selectedInventors.map((inventor) => (
                  <tr key={inventor.value}>
                    <td>{inventor.value}</td>
                    <td>{inventor.label}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Col>
          <Col>
            <Row>
              <Col>
                <label className="form-label">Other Inventors:</label>
                <input
                  type="text"
                  name="otherInventor"
                  className="form-control"
                  value={formData.otherInventor}
                  onChange={handleOtherInventorChange}
                />
                <Button type="button" variant="primary" onClick={addOtherInventor}>
                  Add Other Inventors
                </Button>
                <ul>
                  {formData.otherInventors.split(',').map((inventor, index) => (
                    <li key={index}>{inventor.trim()}</li>
                  ))}
                </ul>
              </Col>

              <Col>
                <label className="form-label">Status:</label>
                <select
                  name="status"
                  className="form-select"
                  value={formData.status}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select Status</option>
                  <option value="Filed">Filed</option>
                  <option value="Granted">Granted</option>
                  <option value="FER Submitted">FER Submitted</option>
                  <option value="Hearing Completed">Hearing Completed</option>
                  <option value="Patents granted and active">Patents granted and active</option>
                  <option value="Patents granted but not renewed">Patents granted but not renewed</option>
                  <option value="Patents Legally Ceased">Patents Legally Ceased</option>
                  <option value="Application FER reply filed">Application FER reply filed</option>
                  <option value="Application in FER hearing">Application in FER hearing</option>
                  <option value="Applications requested for examination">Applications requested for examination</option>
                  <option value="Applications awaiting examination">Applications awaiting examination</option>
                  <option value="Applications published">Applications published</option>
                  <option value="Applications awaiting publication">Applications awaiting publication</option>
                  <option value="Applications awaiting complete specification">Applications awaiting complete specification</option>
                  <option value="Applications refused">Applications refused</option>
                  <option value="Applications Abandoned">Applications Abandoned</option>
                </select>
              </Col>
            </Row>
          </Col>
        </Row>

        {/* New Rows for Published, FER Submitted and Hearing Dates */}
        <Row>
          <Col>
            <label className="form-label">Published Date:</label>
            <input
              type="date"
              name="publishedDate"
              className="form-control"
              value={formData.publishedDate}
              onChange={handleChange}
            />
          </Col>
          <Col>
            <label className="form-label">FER Submitted Date:</label>
            <input
              type="date"
              name="ferSubmittedDate"
              className="form-control"
              value={formData.ferSubmittedDate}
              onChange={handleChange}
            />
          </Col>
        </Row>
        <Row>
          <Col>
            <label className="form-label">Hearing Date:</label>
            <input
              type="date"
              name="hearingDate"
              className="form-control"
              value={formData.hearingDate}
              onChange={handleChange}
            />
          </Col>
          <Col>
            <label className="form-label">Granted Date:</label>
            <input
              type="date"
              name="grantedDate"
              className="form-control"
              value={formData.grantedDate}
              onChange={handleChange}
            />
          </Col>
        </Row>
        <Row>
          <Col>
            <label className="form-label">Patent Number:</label>
            <input
              type="text"
              name="patentNumber"
              className="form-control"
              value={formData.patentNumber}
              onChange={handleChange}
              
            />
          </Col>
          <Col>
            <label className="form-label">Renewal Date:</label>
            <input
              type="date"
              name="renewalDate"
              className="form-control"
              value={formData.renewalDate}
              onChange={handleChange}
            />
          </Col>
        </Row>

        {/* Remaining fields */}
        <Row>
          <Col>
            <Form.Group controlId="formTechnologyReadinessLevel">
              <Form.Label>Technology Readiness Level</Form.Label>
              <Form.Control as="select" name="technologyReadinessLevel" value={formData.technologyReadinessLevel} onChange={handleChange}>
                <option value="" disabled hidden>Select level</option>
                {readinessLevels.map((level, index) => (
                  <option key={index} value={level}>
                    {level}
                  </option>
                ))}
              </Form.Control>
            </Form.Group>
          </Col>
          <Col>
            <label className="form-label">Commercialized:</label>
            <select
              name="commercialized"
              className="form-select"
              value={formData.commercialized}
              onChange={handleChange}
              required
            >
              <option value="no">No</option>
              <option value="yes">Yes</option>
            </select>
          </Col>
        </Row>

        {/* Conditional rendering for commercialization fields */}
        {formData.commercialized === 'yes' && (
          <>
            <Row>
              <Col>
                <label className="form-label">Commercialization Description:</label>
                <textarea
                  name="commercializationDescription"
                  className="form-control"
                  value={formData.commercializationDescription}
                  onChange={handleChange}
                />
              </Col>
            </Row>
            <Row>
              <Col>
                <label className="form-label">Commercialization Percentage:</label>
                <input
                  type="number"
                  name="commercializationPercentage"
                  className="form-control"
                  value={formData.commercializationPercentage}
                  onChange={handleChange}
                />
              </Col>
              <Col>
                <label className="form-label">Funds Generated:</label>
                <input
                  type="number"
                  name="fundsGenerated"
                  className="form-control"
                  value={formData.fundsGenerated}
                  onChange={handleChange}
                />
              </Col>
            </Row>
            <Row>
              <Col>
                <label className="form-label">Commercialization Date:</label>
                <input
                  type="date"
                  name="commercializationDate"
                  className="form-control"
                  value={formData.commercializationDate}
                  onChange={handleChange}
                />
              </Col>
              <Col>
                <label className="form-label">Start Date:</label>
                <input
                  type="date"
                  name="startDate"
                  className="form-control"
                  value={formData.startDate}
                  onChange={handleChange}
                />
              </Col>
            </Row>
            <Row>
              <Col>
                <label className="form-label">File Path:</label>
                <input
                  type="text"
                  name="filePath"
                  className="form-control"
                  value={formData.filePath}
                  readOnly // Make this read-only since we are using file upload
                />
                <input
                  type="file"
                  className="form-control mt-2"
                  onChange={handleFileUpload}
                />
              </Col>
            </Row>
          </>
        )}
        <div className="d-flex justify-content-center mt-4">
          <Button type="submit" variant="primary" style={{ width: '48%' }}>
            {id ? 'Update' : 'Submit'}
          </Button>
          <Button type="button" variant="secondary" style={{ width: '48%', marginLeft: '10px' }}>
            Clear
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default EditPatentForm;